import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {BsFillPersonFill, BsFillChatSquareFill, BsClipboard, BsClipboardCheck} from 'react-icons/bs';
import remarkGfm from 'remark-gfm';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import csharp from 'react-syntax-highlighter/dist/esm/languages/prism/csharp';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup';

// Registrar los lenguajes para el resaltado de sintaxis
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('csharp', csharp);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('tsx', tsx);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('html', markup);

/**
 * Componente para mostrar mensajes individuales en el chat
 * Soporta formato Markdown para los mensajes del asistente
 */
const MessageItem = ({ message, isUser, userName = 'Usuario', agentData }) => {
  const [isMessageCopied, setIsMessageCopied] = useState(false);
  const [copiedCodeKey, setCopiedCodeKey] = useState(null);
  console.log('MessageItem - agentData:', agentData);
  console.log('MessageItem - isUser:', isUser);
  console.log('MessageItem - message:', message);
  // Obtener la fecha formateada para la hora del mensaje
  const formattedTime = new Date(message.timestamp).toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Determinar el nombre del remitente
  const senderName = isUser 
    ? userName 
    : agentData?.name || agentData?.text || message.metadata?.agentName || 'Asistente';
    
  console.log('senderName:', senderName);

  const handleCopyMessageText = () => {
    if (!message || !message.text) return;
    navigator.clipboard.writeText(message.text)
      .then(() => {
        setIsMessageCopied(true);
        setTimeout(() => setIsMessageCopied(false), 2000);
      })
      .catch(err => console.error('Error al copiar el mensaje completo:', err));
  };

  const handleCopyCode = (codeString, key) => {
    navigator.clipboard.writeText(codeString)
      .then(() => {
        setCopiedCodeKey(key);
        setTimeout(() => setCopiedCodeKey(null), 2000);
      })
      .catch(err => console.error('Error al copiar el código:', err));
  };
    
  // Determinar el ícono a mostrar
  const renderIcon = () => {
    try {
      // Para mensajes del usuario
      if (isUser) {
        return <BsFillPersonFill className="w-4 h-4 mr-1 flex-shrink-0" />;
      }
      
      // Para mensajes del asistente
      if (agentData?.icon) {
        try {
          // Si es un componente de React
          const IconComponent = agentData.icon;
          return <IconComponent className="w-4 h-4 mr-1 flex-shrink-0" />;
        } catch (e) {
          console.warn('Error al renderizar el ícono del agente:', e);
        }
      }
      
      // Ícono por defecto para el asistente
      return <BsFillChatSquareFill className="w-4 h-4 mr-1 flex-shrink-0" />;
    } catch (error) {
      console.error('Error en renderIcon:', error);
      return <BsFillChatSquareFill className="w-4 h-4 mr-1 flex-shrink-0" />;
    }
  };

  return (
    <div 
      className={`message-item ${isUser ? 'user' : 'assistant'} mb-4 flex flex-col ${isUser ? 'ml-auto items-end max-w-[85%] md:max-w-[80%]' : 'w-full'}`}
      aria-label={`Mensaje de ${isUser ? 'usuario' : 'asistente'}`}
    >
      {isUser ? (
        // Estructura para mensajes del usuario
        <>
          <div className="flex items-center mb-1 text-sm font-medium text-blue-600 dark:text-blue-400">
            {renderIcon()}
            <span>{senderName}</span>
          </div>
          <div className="bg-blue-100 text-sm text-gray-700 dark:bg-blue-800 dark:text-white p-4 rounded-l-xl rounded-t-xl drop-shadow-sm">
            <p className="whitespace-pre-wrap break-words">{message.text}</p>
          </div>
          <div className="text-xs mt-1 text-gray-500 dark:text-gray-400 text-right">
            {formattedTime}
          </div>
        </>
      ) : (
        // Estructura para mensajes del asistente
        <>
          <div className="flex items-center mb-1 text-sm font-medium text-gray-600 dark:text-gray-400 min-h-5">
            {renderIcon()}
            <span className="whitespace-nowrap overflow-hidden text-ellipsis">{senderName}</span>
          </div>
          <div className="relative group prose dark:prose-invert max-w-none prose-sm text-gray-800 dark:text-gray-100 p-4 leading-relaxed bg-gray-50 dark:bg-slate-800 rounded-xl shadow-sm">
            <button 
              onClick={handleCopyMessageText}
              className="absolute bottom-2 left-2 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-all opacity-0 group-hover:opacity-100 z-10"
              aria-label="Copiar mensaje completo"
            >
              {isMessageCopied ? <BsClipboardCheck size={16} /> : <BsClipboard size={16} />}
            </button>
            <div className="overflow-x-auto mt-1 mb-2">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({node, ...props}) => <a {...props} className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer" />,
                  // Personalización para encabezados y listas para reducir tamaño y espaciado
                  h2: ({node, ...props}) => <h2 {...props} className="text-lg font-semibold mt-2 mb-1" />,
                  h3: ({node, ...props}) => <h3 {...props} className="text-sm font-semibold mt-1 mb-0.5" />,
                  h4: ({node, ...props}) => <h4 {...props} className="text-xs font-semibold mt-1 mb-0.5" />,
                  ul: ({node, ...props}) => <ul {...props} className="list-disc pl-5 my-1" />,
                  li: ({node, ...props}) => <li {...props} className="mb-0.5 leading-snug" />,
                  table: ({node, ...props}) => (
                    <table className="min-w-full border border-gray-300 dark:border-slate-600 rounded-lg overflow-hidden shadow-sm bg-white dark:bg-slate-700">
                      {props.children}
                    </table>
                  ),
                  thead: ({node, ...props}) => (
                    <thead className="bg-gray-100 dark:bg-slate-600 text-gray-900 dark:text-slate-100">
                      {props.children}
                    </thead>
                  ),
                  th: ({node, ...props}) => (
                    <th className="border border-gray-300 dark:border-slate-600 px-4 py-2 font-semibold text-left text-gray-700 dark:text-slate-100 bg-gray-100 dark:bg-slate-600">
                      {props.children}
                    </th>
                  ),
                  td: ({node, ...props}) => (
                    <td className="border border-gray-300 dark:border-slate-600 px-4 py-2 align-top text-gray-700 dark:text-slate-200">
                      {props.children}
                    </td>
                  ),
                  code: ({node, inline, className, children, ...props}) => {
                    const match = /language-(\w+)/.exec(className || '');
                    const codeBlockKey = node.position?.start?.offset || Math.random().toString(); // Unique key for the code block

                    return !inline && match ? (
                      <div className="relative group code-block-wrapper">
                        <button 
                          onClick={() => handleCopyCode(String(children).replace(/\n$/, ''), codeBlockKey)}
                          className="absolute top-2 right-2 p-1 bg-slate-600 hover:bg-slate-500 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity z-10 text-xs"
                          aria-label="Copiar código"
                        >
                          {copiedCodeKey === codeBlockKey ? <BsClipboardCheck size={14} /> : <BsClipboard size={14} />}
                        </button>
                        <SyntaxHighlighter
                          style={oneDark}
                          language={match[1]}
                          PreTag="div"
                          className="p-4 rounded-md overflow-x-auto shadow-sm my-2 text-sm syntax-highlighter-container"
                          showLineNumbers={true}
                          wrapLines={true}
                          wrapLongLines={true}
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      </div>
                    ) : (
                      <code 
                        className={`px-1.5 py-0.5 bg-gray-200 dark:bg-slate-700 text-red-600 dark:text-red-400 rounded text-sm ${className || ''}`}
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  }

                }}
              >
                {message.text}
              </ReactMarkdown>
            </div>
            <div className="text-xs mt-1 text-gray-500 dark:text-gray-400 text-right">
              {formattedTime}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MessageItem;
